import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client from "../client";

export type AppUsageSummaryStatus = "fresh" | "stale" | "generating" | "error";

export interface AppUsageSummaryResult {
  status: AppUsageSummaryStatus;
  summaryText: string | null;
  model: string | null;
  computedAt: string | null;
  expiresAt: string | null;
  lastError: string | null;
}

const POLL_INTERVAL_MS = 3_000;
const POLL_CAP_MS = 60_000;

/**
 * Fetch the AI-generated app-usage summary for a customer. Read-only —
 * the daily 02:30 NY cron is the sole producer. Polls only while the
 * status is "generating" (i.e. while a manual regenerate is in flight).
 */
export function useAppUsageSummary(customerId: string | undefined) {
  return useQuery<AppUsageSummaryResult>({
    queryKey: ["crm", "appUsageSummary", customerId],
    queryFn: () =>
      client
        .get(`/admin/customers/${customerId}/app-usage-summary`)
        .then((r) => r.data),
    enabled: Boolean(customerId),
    refetchInterval: (query) => {
      const data = query.state.data as AppUsageSummaryResult | undefined;
      if (!data || data.status !== "generating") return false;
      const elapsed = Date.now() - (query.state.dataUpdatedAt ?? Date.now());
      if (elapsed > POLL_CAP_MS) return false;
      return POLL_INTERVAL_MS;
    },
    placeholderData: (prev) => prev,
  });
}

/**
 * Force-regenerate this customer's app-usage summary. Useful after a
 * post-deploy probe; otherwise the nightly cron handles it.
 */
export function useRegenerateAppUsageSummary(customerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      client
        .post(`/admin/customers/${customerId}/app-usage-summary/regenerate`)
        .then((r) => r.data as AppUsageSummaryResult),
    onSuccess: (data) => {
      qc.setQueryData(["crm", "appUsageSummary", customerId], data);
      qc.invalidateQueries({ queryKey: ["crm", "appUsageSummary", customerId] });
    },
  });
}
